// Telling a dead session apart from a plain request failure.
//
// The app stores its JWT in localStorage and attaches it to every authenticated
// request. When that token expires or is otherwise rejected, the server answers
// 401 — and until this existed, the athlete was left on the page reading the raw
// "Invalid token" string with the nav still intact and no way out. This is the
// one decision behind the global handler that fixes that: is a given failure a
// dead session, or something the calling screen should handle itself?
//
// The signal is the Authorization header, NOT the message. The server sends
// several different 401 bodies for auth failures — "Invalid token", "Invalid
// token claims", "Authorization header required" (middleware/auth.go) — and a
// fourth would slip a text match. What every session-expiry 401 shares, and what
// a bad-login 401 does not, is that the failed request CARRIED a bearer token:
// login, register, forgot-password and reset-password are unauthenticated, so
// their 401s are credential errors the forms explain in place, and must not
// trigger a logout.

/** The message shown on the login screen after a session is dropped. */
export const SESSION_EXPIRED_NOTICE = 'Your session has expired. Please sign in again.';

/** Did the request carry an Authorization header? Case- and shape-tolerant. */
function hadAuthorization(config) {
  // axios may hand back a plain object or an AxiosHeaders instance, and header
  // casing is not guaranteed — so look at the keys case-insensitively rather
  // than reading `.Authorization` off whatever this is.
  const headers = config?.headers ?? {};
  return Object.keys(headers).some((key) => key.toLowerCase() === 'authorization');
}

/**
 * Is this failure a dropped session — a 401 to a request we authenticated?
 *
 * True only when the response is 401 AND the request sent an Authorization
 * header. A 401 without one is an unauthenticated endpoint's own answer (a bad
 * login); any other status is not a session problem at all.
 */
export function isSessionExpiry(error) {
  return error?.response?.status === 401 && hadAuthorization(error?.config);
}
